/**
 * Schema Grouping Restructure — given a database node's current children and
 * the list of preloaded user objects for that database, produces a new list
 * of children that either:
 *   - groups Tables/Views/Programmability under schema-container folders, or
 *   - keeps the current flat layout,
 * depending on the active grouping mode and per-database heuristic.
 *
 * Security folders (database-security-folder, schemas-folder, users-folder,
 * database-roles-folder) and Oracle-specific folders (packages, sequences,
 * synonyms, db-links, materialized-views, types, directories) are PRESERVED
 * at the database level in both branches.
 *
 * Rebuilt folders keep the expansion state (and, when lazy, the loaded children)
 * of the folder they replace — see `carryOverViewState`. Pass `resetFolders` to
 * opt out, which an explicit refresh does because its cached children are stale.
 */

import { DatabaseObject } from '../../database-objects/models/databaseObjectTypes';
import { isSameChildList } from '../../database-objects/services/databaseTreeBuilder';
import { DatabaseEngineId } from '../../../core/engines/engineTypes';
import { isKustoProxyConnection } from '../../connections/services/kustoProxyConnection';
import { ObjectExplorerGroupingMode } from '../../../store/appStore';
import {
  DatabaseExplorerNode,
  createDatabaseObjectTypeFolders,
} from '../models/DatabaseTreeModels';
import {
  shouldGroupBySchema,
  buildSchemaContainersForDatabase,
} from './schemaGrouping';

const OBJECT_TYPE_FOLDER_TYPES = new Set<string>([
  'tables-folder',
  'views-folder',
  'programmability-folder',
]);

// Types owned by the grouping feature that must be regenerated each pass
// rather than preserved — otherwise flipping Always→Never leaves stale
// schema-container folders alongside the fresh flat folders.
const GROUPING_OWNED_FOLDER_TYPES = new Set<string>([
  ...OBJECT_TYPE_FOLDER_TYPES,
  'schema-container-folder',
]);

export interface ApplySchemaGroupingArgs {
  currentChildren: DatabaseExplorerNode[];
  objects: DatabaseObject[];
  mode: ObjectExplorerGroupingMode;
  connectionId: string;
  database: string;
  engine?: DatabaseEngineId;
  /**
   * Discard expansion state and lazily-loaded children instead of carrying them
   * over. Set by an explicit refresh, where the tree's cached children are known
   * to be stale and must be re-fetched on the next expand.
   */
  resetFolders?: boolean;
}

/**
 * Re-attaches view state that a rebuild would otherwise throw away, matching
 * old and new nodes by their (deterministic) ids.
 *
 * A rebuilt node that carries no children of its own is a *lazy* folder — the
 * fresh copy holds nothing the old one doesn't. We return the OLD node itself
 * rather than a merged copy, so its identity (and its whole loaded subtree)
 * survives untouched. Identity matters: handing React a new object for an
 * unchanged row remounts it, and a click dispatched at that moment is
 * swallowed — an expand the user has to perform twice.
 *
 * Grouped containers are populated from `objects`, so their fresh contents win
 * and only `isExpanded` carries over, recursively.
 *
 * Without this, the object preloader's restructure pass (~1-2s after connect)
 * silently collapses any folder expanded in that window.
 */
function carryOverViewState(
  next: DatabaseExplorerNode[],
  current: DatabaseExplorerNode[],
): DatabaseExplorerNode[] {
  if (current.length === 0) return next;
  const byId = new Map(current.map((node) => [node.id, node]));

  return next.map((node) => {
    const old = byId.get(node.id);
    if (!old) return node;
    const nextChildren = (node.children ?? []) as DatabaseExplorerNode[];
    const oldChildren = (old.children ?? []) as DatabaseExplorerNode[];

    // A rebuilt node with no children of its own is a lazy folder: the fresh
    // copy holds nothing the old one doesn't.
    const children =
      nextChildren.length === 0 ? oldChildren : carryOverViewState(nextChildren, oldChildren);

    // Nothing new anywhere below — keep the old node so its identity survives.
    if (isSameChildList(children, oldChildren)) return old;

    return { ...node, isExpanded: old.isExpanded, children } as DatabaseExplorerNode;
  });
}


/**
 * Computes the new children list for a database node under the given mode.
 * The function is pure — it does not mutate the input tree.
 */
export function applySchemaGroupingToDatabaseNode(
  args: ApplySchemaGroupingArgs,
): DatabaseExplorerNode[] {
  const { currentChildren, objects, mode, connectionId, database, engine, resetFolders } = args;

  // Everything that isn't owned by the grouping feature is preserved as-is
  // (security, oracle-specific, agent, etc.). This intentionally drops any
  // existing schema-container folders so they're rebuilt from scratch on
  // every call.
  const preserved = currentChildren.filter((c) => !GROUPING_OWNED_FOLDER_TYPES.has(c.type));
  const rebuilt = currentChildren.filter((c) => GROUPING_OWNED_FOLDER_TYPES.has(c.type));
  const withViewState = (next: DatabaseExplorerNode[]) =>
    resetFolders ? next : carryOverViewState(next, rebuilt);

  if (!shouldGroupBySchema(mode, objects)) {
    // Flat layout: ensure the three standard object-type folders exist. They're
    // rebuilt from scratch so that flipping Always→Never can't leave stale
    // schema containers behind; `withViewState` then re-attaches the expansion
    // and lazily-loaded children of any folder that already existed. Security
    // folders are kept as-is (not duplicated) by filtering them out of the
    // fresh `createDatabaseObjectTypeFolders` result below.
    const fresh = createDatabaseObjectTypeFolders(connectionId, database, engine, isKustoProxyConnection(connectionId));
    const freshObjectTypeFolders = fresh.filter((c) =>
      OBJECT_TYPE_FOLDER_TYPES.has(c.type),
    );
    return [
      ...withViewState(freshObjectTypeFolders as unknown as DatabaseExplorerNode[]),
      ...preserved,
    ];
  }

  const containers = buildSchemaContainersForDatabase({
    objects,
    connectionId,
    database,
    engine,
  });

  return [
    ...withViewState(containers as unknown as DatabaseExplorerNode[]),
    ...preserved,
  ];
}
